import { ProductType } from "@/analytics";
import { buildCarVirtualPvData } from "@/utils/car/virtualPvUtils";
import { TrackingEvent } from "../../constants/events";
import { pushToDataLayer } from "../../providers/google/utils";

/**
 * Google Analytics - Car Provider
 * Araç kategorisi için Google Analytics event'lerini yönetir
 */
export class CarGoogleProvider {
  track(event, data) {
    switch (event) {
      case TrackingEvent.VIRTUAL_PV:
        this.trackVirtualPv(data);
        break;
      case TrackingEvent.SEARCH_RESULT:
        this.trackSearchResult(data);
        break;
      case TrackingEvent.CHECK_RESERVATION:
        this.trackCheckReservation(data);
        break;
      case TrackingEvent.VIEW_ITEM_LIST:
        this.trackViewItemList(data);
        break;
      case TrackingEvent.SELECT_ITEM:
        this.trackSelectItem(data);
        break;
      case TrackingEvent.VIEW_ITEM:
        this.trackViewItem(data);
        break;
      case TrackingEvent.ADD_TO_CART:
        this.trackAddToCart(data);
        break;
      case TrackingEvent.BEGIN_CHECKOUT:
        this.trackBeginCheckout(data);
        break;
      case TrackingEvent.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(data);
        break;
      case TrackingEvent.PURCHASE:
        this.trackPurchase(data);
        break;
      case TrackingEvent.FILTER_APPLIED:
        this.trackFilterApplied(data);
        break;
      case TrackingEvent.SORTING_APPLIED:
        this.trackSortingApplied(data);
        break;
      case TrackingEvent.PROMOTIONAL_BANNER_CLICKED:
        this.trackPromotionalBannerClicked(data);
        break;
      case TrackingEvent.SHOWN_ERROR:
        this.trackShownError(data);
        break;
      case TrackingEvent.SELECT_COUPON:
      case TrackingEvent.VIEW_COUPON:
      case TrackingEvent.COUPON_APPLIED:
        this.trackCouponEvent(event, data);
        break;
      default:
        this.trackGenericEvent(event, data);
    }
  }

  trackVirtualPv(data) {
    Promise.resolve(buildCarVirtualPvData(data)).then((virtualPvData) => {
      pushToDataLayer(TrackingEvent.VIRTUAL_PV, virtualPvData);
    });
  }

  trackSearchResult(data) {
    const event =
      data?.search_result_number > 0
        ? TrackingEvent.SEARCH_RESULT
        : TrackingEvent.NO_SEARCH_RESULT;

    pushToDataLayer(event, {
      search_category: ProductType.CAR,
      event: "custom_event",
      event_category: "search",
      event_action: event,
      search_destination: data?.search_destination,
      search_term: null,
      start_date: data?.pickUpDateTime + ":00.000Z",
      end_date: data?.dropOffDateTime + ":00.000Z",
      drop_off_location_name: data?.dropOffLocation,
      drop_off_city_name: data?.dropOffCity,
      pick_up_location_name: data?.pickUpLocation,
      pick_up_city_name: data?.pickUpCity,
      different_drop_off_location: data?.isDifferentLocation ? "yes" : "no",
      ...(data?.search_result_number > 0 && {
        search_result_number: data?.search_result_number,
      }),
    });
  }

  trackCheckReservation(data) {
    pushToDataLayer(TrackingEvent.CHECK_RESERVATION, {
      event: "custom_event",
      event_category: "custom_event",
      event_action: TrackingEvent.CHECK_RESERVATION,
      pnr_number: data.pnr,
      reservation_category: "car",
    });
  }

  trackViewItemList(data) {
    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    // Eğer caller doğrudan ecommerce objesini verdiyse aynen geçir
    let ecommerce = data?.ecommerce;

    pushToDataLayer(TrackingEvent.VIEW_ITEM_LIST, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item_list",
      ecommerce,
    });
  }

  trackSelectItem(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.SELECT_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "select_item",
      ecommerce: data.ecommerce,
    });
  }

  trackViewItem(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.VIEW_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item",
      ecommerce: data.ecommerce,
    });
  }

  trackAddToCart(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_CART, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_cart",
      ecommerce: data.ecommerce,
    });
  }

  trackBeginCheckout(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.BEGIN_CHECKOUT, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "begin_checkout",
      ecommerce: data.ecommerce,
    });
  }

  trackAddPaymentInfo(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_PAYMENT_INFO, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_payment_info",
      ecommerce: data.ecommerce,
    });
  }

  trackPurchase(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.PURCHASE, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "purchase",
      ecommerce: data.ecommerce,
    });
  }

  trackFilterApplied(data) {
    pushToDataLayer(TrackingEvent.FILTER_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "filter_applied",
      filter_product_category: ProductType.CAR,
      filter_category: data?.filterName,
      filter_subcategory: data?.label,
      sorting_type: data?.sortingType,
    });
  }

  trackSortingApplied(data) {
    pushToDataLayer(TrackingEvent.SORTING_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "sorting_applied",
      filter_product_category: ProductType.CAR,
      filter_category: "sorting",
      filter_subcategory: data?.sortingType,
    });
  }

  trackPromotionalBannerClicked(data) {
    pushToDataLayer(TrackingEvent.PROMOTIONAL_BANNER_CLICKED, {
      event: "custom_event",
      event_category: "switch",
      event_action: `switch_to_${data?.type}`,
      [`${data?.type}_route`]: `${data?.from}-${data?.to}`,
    });
  }

  trackShownError(data) {
    pushToDataLayer(TrackingEvent.PROMOTIONAL_BANNER_CLICKED, {
      event: "custom_event",
      event_category: "error",
      event_action: `shown_error`,
      error_message: data?.errorMessage || null,
      error_type: data?.errorType || null,
      error_location: data?.errorLocation || null,
    });
  }

  trackCouponEvent(event, data) {
    // Extract coupon data from raw offer object
    const offer = data.offer || {};
    const currencyCode = data.currencyCode || "TRY";

    const couponName = offer?.code || "";
    const couponValue = offer?.discountText || "";

    pushToDataLayer(event, {
      event: "custom_event",
      event_category: "coupon",
      event_action: event,
      coupon_name: couponName,
      coupon_value: null,
      coupon_currency: currencyCode,
      coupon_category: "car",
    });
  }

  trackGenericEvent(event, data) {
    pushToDataLayer(event, data);
  }
}
